import { DriverGroupDto, OrganizationUserDto, UserStatus } from '@cargovibe/shared-types';
import { formatFullName } from '@/shared/services/formatFullName';
import type { SortDirection, UserSortKey } from '@/modules/users/components/UsersTable';

const USER_STATUS_ORDER: UserStatus[] = Object.values(UserStatus);

interface FilterShownUserTableEntriesParams {
  users: OrganizationUserDto[] | undefined;
  searchTerm: string;
  sortKey: UserSortKey;
  sortDirection: SortDirection;
  activeGroup: DriverGroupDto | null;
  isSelectingMembers: boolean;
}

function compareUsers(
  userA: OrganizationUserDto,
  userB: OrganizationUserDto,
  key: UserSortKey
): number {
  if (key === 'lastname') {
    return (
      userA.lastname.localeCompare(userB.lastname) || userA.firstname.localeCompare(userB.firstname)
    );
  }
  return USER_STATUS_ORDER.indexOf(userA.status) - USER_STATUS_ORDER.indexOf(userB.status);
}

export function filterShownUserTableEntries({
  users,
  searchTerm,
  sortKey,
  sortDirection,
  activeGroup,
  isSelectingMembers,
}: FilterShownUserTableEntriesParams): OrganizationUserDto[] {
  if (!users) return [];

  const lowerSearchTerm: string = searchTerm.toLowerCase();
  const searched: OrganizationUserDto[] = lowerSearchTerm
    ? users.filter(
        (user: OrganizationUserDto): boolean =>
          formatFullName(user.firstname, user.lastname).toLowerCase().includes(lowerSearchTerm) ||
          (user.email?.toLowerCase().includes(lowerSearchTerm) ?? false)
      )
    : users;

  const memberIds: Set<string> = new Set<string>(activeGroup?.memberIds ?? []);

  if (activeGroup && isSelectingMembers) {
    return [...searched].sort(
      (userA: OrganizationUserDto, userB: OrganizationUserDto): number =>
        Number(memberIds.has(userB.id)) - Number(memberIds.has(userA.id)) ||
        formatFullName(userA.firstname, userA.lastname).localeCompare(
          formatFullName(userB.firstname, userB.lastname)
        )
    );
  }

  const directionMultiplier: 1 | -1 = sortDirection === 'asc' ? 1 : -1;
  const scoped: OrganizationUserDto[] = activeGroup
    ? searched.filter((user: OrganizationUserDto): boolean => memberIds.has(user.id))
    : searched;

  return [...scoped].sort(
    (userA: OrganizationUserDto, userB: OrganizationUserDto): number =>
      compareUsers(userA, userB, sortKey) * directionMultiplier
  );
}
